import { useState, type ReactNode } from 'react';
import { getTimeline } from '../lifeos/api.js';
import { clock, dayLabel, humanize, ymdInHomeZone } from '../lifeos/format.js';
import { sphereColor, sphereName, useSpheres } from '../lifeos/spheres.js';
import { timelineText } from '../lifeos/timeline.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Chip, EmptyState, Group, Icon, IconButton, Loader, MoreRow, Page, Row, Segmented, Sheet, useParamSetter } from '../ui/index.js';
import type { TimelineItem, TimelineKind } from '../lifeos/types.js';

const RANGES = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
] as const;
type Range = (typeof RANGES)[number]['value'];

export const TIMELINE_KINDS: readonly { kind: TimelineKind; label: string }[] = [
  { kind: 'message', label: 'Messages' },
  { kind: 'task_done', label: 'Tasks done' },
  { kind: 'event', label: 'Events' },
  { kind: 'payment', label: 'Payments' },
  { kind: 'work', label: 'Work' },
  { kind: 'workout', label: 'Workouts' },
  { kind: 'obligation_done', label: 'Paid' },
  { kind: 'agent_run', label: 'Agent runs' },
  { kind: 'life_note', label: 'Notes' },
  { kind: 'journal', label: 'Journal' },
];

/** Group rows (newest first) by their America/New_York day. */
export function groupByDay(items: readonly TimelineItem[]): [string, TimelineItem[]][] {
  const out = new Map<string, TimelineItem[]>();
  for (const it of [...items].sort((a, b) => b.at.localeCompare(a.at))) {
    const day = ymdInHomeZone(new Date(it.at));
    const list = out.get(day);
    if (list) list.push(it);
    else out.set(day, [it]);
  }
  return [...out.entries()];
}

/** Rows a day shows before "Show N more". */
export const DAY_SHOWN = 8;

/** One timeline entry as a row: time, kind dot, what happened in words. */
export function TimelineRow({ it, when }: { it: TimelineItem; when?: ReactNode }): JSX.Element {
  const t = timelineText(it);
  return (
    <Row
      className="la-tl"
      leading={
        <span className="la-tl__lead">
          <span className="la-tl__time num">{when ?? clock(it.at)}</span>
          <span className={`la-tl__dot lo-kind--${it.kind}`} title={humanize(it.kind)} aria-hidden="true" />
        </span>
      }
      title={t.title}
      sub={t.detail ?? undefined}
    />
  );
}

/**
 * Timeline (`/timeline`): what happened when, across messages, money, health,
 * work and agents. The range, kinds and sphere live in the URL; kinds and
 * sphere sit behind one Filter button (a sheet) so the stream comes first,
 * and each active filter shows as a removable chip.
 * Message titles are third-party text and render as plain text.
 */
export default function Timeline(): JSX.Element {
  const [params, setParam] = useParamSetter();
  const range: Range = RANGES.find((r) => r.value === params.get('days'))?.value ?? '7';
  const kinds = params.get('kinds')?.split(',').filter(Boolean) ?? [];
  const sphere = params.get('sphere');
  const spheres = useSpheres();
  const filtered = kinds.length + (sphere ? 1 : 0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const items = useLoad(() => {
    const to = new Date(Date.now() + 86_400_000);
    const from = new Date(Date.now() - Number(range) * 86_400_000);
    return getTimeline({
      from: from.toISOString(),
      to: to.toISOString(),
      sphere: sphere ?? undefined,
      kinds: kinds.length ? kinds.join(',') : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, kinds.join(','), sphere]);

  const toggleKind = (k: string): void => {
    const next = kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k];
    setParam('kinds', next.join(',') || null);
  };

  return (
    <Page
      title="Timeline"
      actions={<IconButton icon="filter" label={filtered > 0 ? `Filter, ${filtered} on` : 'Filter'} active={filtered > 0} onClick={() => setFiltersOpen(true)} />}
    >
      {filtered > 0 && (
        <div className="ui-chips" aria-label="Active filters">
          {kinds.map((k) => (
            <Chip key={k} on onClick={() => toggleKind(k)} label={`Clear ${TIMELINE_KINDS.find((x) => x.kind === k)?.label ?? k}`}>
              <span className={`ui-dot lo-kind--${k}`} aria-hidden="true" />
              {TIMELINE_KINDS.find((x) => x.kind === k)?.label ?? humanize(k)}
              <Icon name="x" size={14} />
            </Chip>
          ))}
          {sphere && (
            <Chip on dot={sphereColor(spheres, sphere)} onClick={() => setParam('sphere', null)} label={`Clear sphere ${sphereName(spheres, sphere)}`}>
              {sphereName(spheres, sphere)}
              <Icon name="x" size={14} />
            </Chip>
          )}
        </div>
      )}
      <Segmented label="Range" value={range} options={RANGES} onChange={(v) => setParam('days', v === '7' ? null : v)} />
      <Loader state={items}>
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState icon="history" title="Nothing in this range." hint={filtered > 0 ? 'Try clearing a filter.' : undefined} />
          ) : (
            groupByDay(rows).map(([day, list]) => <Day key={day} day={day} list={list} />)
          )
        }
      </Loader>
      {filtersOpen && (
        <Sheet title="Filter" onClose={() => setFiltersOpen(false)} action={{ label: 'Done', onClick: () => setFiltersOpen(false) }}>
          <p className="la-sheet__label">Show only</p>
          <ul className="ui-group ui-group--flush" aria-label="Kinds">
            {TIMELINE_KINDS.map((k) => (
              <li key={k.kind} className="ui-swipe">
                <button type="button" className="ui-pick" aria-pressed={kinds.includes(k.kind)} onClick={() => toggleKind(k.kind)}>
                  <span className={`ui-dot lo-kind--${k.kind}`} aria-hidden="true" />
                  <span className="ui-pick__label">{k.label}</span>
                  {kinds.includes(k.kind) && <Icon name="check" size={20} className="ui-pick__check" />}
                </button>
              </li>
            ))}
          </ul>
          {spheres.length > 0 && (
            <>
              <p className="la-sheet__label">Sphere</p>
              <ul className="ui-group ui-group--flush" aria-label="Sphere">
                {[{ key: null as string | null, name: 'All spheres', color: undefined as string | undefined }, ...spheres.map((s) => ({ key: s.key as string | null, name: s.name, color: s.color ?? undefined }))].map(
                  (o) => (
                    <li key={o.key ?? 'all'} className="ui-swipe">
                      <button type="button" className="ui-pick" aria-pressed={sphere === o.key} onClick={() => setParam('sphere', o.key)}>
                        <span className="ui-dot" style={{ background: o.color ?? 'var(--text-muted)' }} aria-hidden="true" />
                        <span className="ui-pick__label">{o.name}</span>
                        {sphere === o.key && <Icon name="check" size={20} className="ui-pick__check" />}
                      </button>
                    </li>
                  ),
                )}
              </ul>
            </>
          )}
        </Sheet>
      )}
    </Page>
  );
}

function Day({ day, list }: { day: string; list: TimelineItem[] }): JSX.Element {
  const [all, setAll] = useState(false);
  const shown = all ? list : list.slice(0, DAY_SHOWN);
  const today = ymdInHomeZone(new Date());
  return (
    <Group header={day === today ? 'Today' : dayLabel(day)} label={dayLabel(day)}>
      {shown.map((it) => (
        <TimelineRow key={`${it.refTable}:${it.refId}`} it={it} />
      ))}
      <MoreRow count={list.length - shown.length} onClick={() => setAll(true)} />
    </Group>
  );
}
